import React, { useState, useEffect, useRef } from 'react';
import { Button } from 'reactstrap';
import { ToastComponent } from '@syncfusion/ej2-react-notifications';
import i18n from 'i18next';
import { ListViewComponent } from '@syncfusion/ej2-react-lists';

import EditIcon from '../../../assets/icons/edit.svg?react';
import DeleteIcon from '../../../assets/icons/delete.svg?react';
import AddIcon from '../../../assets/icons/add-circle.svg?react';
import { Confirmation } from '../../../components/notification';
import {
  getEventTypes,
  addorUpdateEventType,
  deleteEventType,
  updateEventTypeColorMode
} from '../../../services/accounts';
import { EVENT_TYPE_COLOR_MODES } from '../../../constants/eventTypeColorModes';
import EventTypeVizCard from './EventTypeVizCard';
import EventTypeDialogForm from './EventTypeDialogForm';
import './eventtype.scss';
import FirmaLogo from '../../../assets/icons/firma/firma-logo.svg?react';

const MAX_NAME_LENGTH = 60;
const PALETTE_COLORS = [
  '#E74856',
  '#FF8C00',
  '#FFAB45',
  '#FFF100',
  '#47D041',
  '#30C6CC',
  '#73AA24',
  '#00BCF2',
  '#8764B8',
  '#F495BF',
  '#A0AEB2',
  '#004B60',
  '#B1ADAB',
  '#5D5A58',
  '#000000',
  '#750B1C',
  '#CA5010',
  '#AB620D',
  '#C19C00',
  '#004B1C',
  '#004B50',
  '#0B6A0B',
  '#002050',
  '#32145A',
  '#5C005C',
];
const PALETTE_COLUMNS = Math.ceil(PALETTE_COLORS.length / 2);
const TOAST_POSITION = { X: 'Right', Y: 'Top' };
const TOAST_ANIMATION = { hide: { duration: '1' }, show: { duration: '200' } };

function Eventtype({ email, lexonToken, colorMode: colorModeProp, onColorModeChange: onColorModeChangeProp, cancel, getlistEventTypes }) {
  const [formMode, setFormMode] = useState(null);
  const [idEvent, setIdEvent] = useState(undefined);
  const [nameValue, setNameValue] = useState('');
  const [color, setColor] = useState(undefined);
  const [errorName, setErrorName] = useState('');
  const [data, setData] = useState([]);
  const [deleteData, setDeleteData] = useState(undefined);
  const [showNotification, setShowNotification] = useState(false);
  const [colorMode, setColorMode] = useState(colorModeProp || EVENT_TYPE_COLOR_MODES.DOT);
  const [pendingColorMode, setPendingColorMode] = useState(colorModeProp || EVENT_TYPE_COLOR_MODES.DOT);

  const listEventTypeRef = useRef(null);
  const toastRef = useRef(null);
  const colorPickerRef = useRef(null);
  const eventTypeDataRef = useRef([]);

  const isFormOpen = formMode !== null;

  useEffect(() => {
    loadEventTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (colorModeProp && colorModeProp !== colorMode) {
      setColorMode(colorModeProp);
      setPendingColorMode(colorModeProp);
    }
  }, [colorModeProp]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (formMode === 'edit' && colorPickerRef.current && color) {
      colorPickerRef.current.value = color;
    }
  }, [formMode, color]);

  useEffect(() => {
    if (!isFormOpen && listEventTypeRef.current) {
      listEventTypeRef.current.dataSource = eventTypeDataRef.current.sort((a, b) => a.Text.localeCompare(b.Text));
    }
  }, [isFormOpen]);

  function getToast(index) {
    const toasts = [
      { content: i18n.t('schedule.toast-processing'), cssClass: 'e-toast-black', icon: '' },
      { content: i18n.t('schedule.toast-process-complete'), cssClass: 'e-toast-success', icon: 'lf-icon lf-icon-check-round' },
      { content: i18n.t('schedule.toast-process-error'), cssClass: 'e-toast-danger', icon: 'lf-icon lf-icon-close-round' },
      { content: i18n.t('schedule.toast-process-error-exist'), cssClass: 'e-toast-danger', icon: 'lf-icon lf-icon-close-round' },
      { content: i18n.t('schedule.toast-create-event-type-complete'), cssClass: 'e-toast-success', icon: 'lf-icon lf-icon-check-round' },
      { content: i18n.t('schedule.toast-edit-event-type-complete'), cssClass: 'e-toast-success', icon: 'lf-icon lf-icon-check-round' },
      { content: i18n.t('schedule.toast-delete-event-type-complete'), cssClass: 'e-toast-success', icon: 'lf-icon lf-icon-check-round' }
    ];
    return toasts[index];
  }

  function showError() {
    if (toastRef.current) {
      toastRef.current.showProgressBar = false;
      toastRef.current.hide('All');
      toastRef.current.timeOut = 1000;
      toastRef.current.show(getToast(2));
    }
  }

  function loadEventTypes() {
    getEventTypes(email, lexonToken)
      .then(result => {
        const eventTypes = Array.isArray(result?.data?.eventTypes)
          ? result.data.eventTypes
          : [];
        const eventTypeColorMode =
          result?.data?.eventTypeColorMode === EVENT_TYPE_COLOR_MODES.BACKGROUND
            ? EVENT_TYPE_COLOR_MODES.BACKGROUND
            : EVENT_TYPE_COLOR_MODES.DOT;
        setColorMode(eventTypeColorMode);
        bindData(eventTypes);
      })
      .catch(error => {
        console.error('error ->', error);
        bindData([]);
      });
  }

  function bindData(items) {
    const safeItems = Array.isArray(items) ? items : [];
    eventTypeDataRef.current = safeItems.map(evt => ({
      Id: evt.idEvent,
      Text: evt.name,
      Color: evt.color
    }));
    setData([...eventTypeDataRef.current]);
    if (listEventTypeRef.current) {
      listEventTypeRef.current.dataSource = eventTypeDataRef.current.sort((a, b) => a.Text.localeCompare(b.Text));
      listEventTypeRef.current.refresh();
    }
  }

  function validateName(value) {
    if (value.length > MAX_NAME_LENGTH) {
      return i18n.t('eventtype.error_name_max_chars');
    }
    if (value.trim().length > 0) {
      const isDuplicate = eventTypeDataRef.current.some(
        e =>
          e.Text.toLowerCase() === value.trim().toLowerCase() &&
          e.Id !== idEvent
      );
      if (isDuplicate) {
        return i18n.t('eventtype.error_name_exists');
      }
    }
    return '';
  }

  function handleNameChange(value) {
    const error = !value.trim()
      ? i18n.t('eventtype.error_name_empty')
      : validateName(value);
    setNameValue(value);
    setErrorName(error);
  }

  function handleClearName() {
    setNameValue('');
    setErrorName(i18n.t('eventtype.error_name_empty'));
  }

  function handleColorChange(args) {
    setColor(args.currentValue.hex);
  }

  function handleBeforeTileRender(args) {
    args.element.classList.add('e-rounded-palette');
  }

  function openNewForm() {
    setFormMode('new');
    setIdEvent(undefined);
    setNameValue('');
    setColor(undefined);
    setErrorName('');
  }

  function openEditForm(item) {
    setFormMode('edit');
    setIdEvent(item.Id);
    setNameValue(item.Text);
    setColor(item.Color);
    setErrorName('');
  }

  function closeForm() {
    setFormMode(null);
    setIdEvent(undefined);
    setNameValue('');
    setColor(undefined);
    setErrorName('');
  }

  function handleAddOrUpdate() {
    const name = (nameValue || '').trim();

    if (!name) {
      setErrorName(i18n.t('eventtype.error_name_empty'));
      return;
    }

    const error = validateName(name);
    if (error) {
      setErrorName(error);
      return;
    }

    if (!color) return;

    const dataEventTypeAPI =
      idEvent != undefined
        ? { email, eventType: { idEvent, name, color } }
        : { email, eventType: { name, color } };

    addorUpdateEventType(dataEventTypeAPI, lexonToken)
      .then(result => {
        if (result.errors.length > 0) {
          if (result.errors[0].errorcode === 'EventExist') {
            setErrorName(i18n.t('eventtype.error_name_exists'));
            return;
          }
          throw true;
        }

        toastRef.current.timeOut = 1000;

        if (idEvent != undefined) {
          toastRef.current.show(getToast(5));
          const item = eventTypeDataRef.current.find(e => e.Id == idEvent);
          if (item) {
            item.Text = name;
            item.Color = color;
          }
        } else {
          toastRef.current.show(getToast(4));
          eventTypeDataRef.current.push({
            Id: result.data.idEvent,
            Text: name,
            Color: color
          });
        }

        setFormMode(null);
        setIdEvent(undefined);
        setNameValue('');
        setErrorName('');
         if (listEventTypeRef.current) {
          listEventTypeRef.current.dataSource = eventTypeDataRef.current.sort((a, b) => a.Text.localeCompare(b.Text));
          listEventTypeRef.current.refresh();
        }
        getlistEventTypes?.();
      })
      .catch(() => {
        showError();
      });
  }

  function handleDeleteRequest(item) {
    setShowNotification(true);
    setDeleteData(item);
  }

  function handleDeleteConfirm(item) {
    deleteEventType({ idEvent: item.Id, email }, lexonToken)
      .then(() => {
        eventTypeDataRef.current = eventTypeDataRef.current.filter(x => x.Id !== item.Id);
        toastRef.current.hide('All');
        toastRef.current.timeOut = 1000;
        toastRef.current.show(getToast(6));
        setData([...eventTypeDataRef.current]);
        setShowNotification(false);
        setDeleteData(undefined);
        setFormMode(null);
        setIdEvent(undefined);
        setNameValue('');
        setErrorName('');
        if (listEventTypeRef.current) {
          listEventTypeRef.current.dataSource = eventTypeDataRef.current.sort((a, b) => a.Text.localeCompare(b.Text));
          listEventTypeRef.current.refresh();
        }
        getlistEventTypes?.();
      })
      .catch(() => {
        showError();
        setShowNotification(false);
        setDeleteData(undefined);
      });
  }

  async function handleSave() {
    if (colorMode !== pendingColorMode) {
      try {
        const result = await updateEventTypeColorMode(email, pendingColorMode, lexonToken);
        if (Array.isArray(result?.errors) && result.errors.length > 0) {
          throw new Error();
        }
        setColorMode(pendingColorMode);
        if (typeof onColorModeChangeProp === 'function') {
          onColorModeChangeProp(pendingColorMode);
        }
      } catch {
        showError();
      }
    }
    cancel();
  }

  function listTemplate(item) {
    return (
      <div className="event-type-item">
        <div style={{ border: `1px solid ${item.Color}` }} className="dot-container">
          <span style={{ backgroundColor: item.Color }} className="dot"></span>
        </div>
        <div className="d-flex justify-content-between align-items-center w-100 ml-3">
          <span className="inter-regular-14 text-color-grey-800">{item.Text}</span>
          <div className="event-type-item-action d-flex">
            <div
              className="event-type-action-icon event-type-action-icon-edit cursor-pointer mr-2"
              onClick={() => openEditForm(item)}
            />
            <div
              className="event-type-action-icon event-type-action-icon-delete cursor-pointer"
              onClick={() => handleDeleteRequest(item)}
            />
            <span className="id hidden">{item.Id}</span>
          </div>
        </div>
      </div>
    );
  }

  const dialogTitle = formMode === 'new' ? i18n.t('eventtype.newevent') : i18n.t('eventtype.modify_title');
  const submitText = formMode === 'new' ? i18n.t('eventtype.newevent') : i18n.t('eventtype.modify_button');
  const nameIsValid = nameValue && nameValue.trim().length > 0 && nameValue.length <= MAX_NAME_LENGTH && !errorName;
  const canSubmit = !!nameIsValid && !!color;

  return (
    <div className="event-type-wrapper">
      <div className="event-type-body">
        <div className="event-type-section-header">
          <h3 className="inter-semibold-16 text-color-blue-lefebvre mb-0">
            {i18n.t('eventtype.title')}
          </h3>
          <div className="btn-new-event-type btn-action-container">
            <Button
              className="btn-outline-primary inter-regular-14"
              color="secondary"
              disabled={isFormOpen}
              title={i18n.t('eventtype.newevent')}
              onClick={openNewForm}
            >
              {i18n.t('eventtype.newevent')}
            </Button>
          </div>
        </div>
        <hr className="event-type-section-divider" />
        <p className="inter-regular-14 text-color-grey-800 mt-2 mb-3">
          {i18n.t('eventtype.title_text')}
        </p>

        {isFormOpen && (
          <EventTypeDialogForm
            title={dialogTitle}
            submitText={submitText}
            nameValue={nameValue}
            errorName={errorName}
            color={color}
            paletteColors={PALETTE_COLORS}
            paletteColumns={PALETTE_COLUMNS}
            canSubmit={canSubmit}
            colorPickerRef={(scope) => { colorPickerRef.current = scope; }}
            onNameChange={handleNameChange}
            onClearName={handleClearName}
            onBeforeTileRender={handleBeforeTileRender}
            onColorChange={handleColorChange}
            onCancel={closeForm}
            onSubmit={handleAddOrUpdate}
          />
        )}

        <div className={`list-view-container${isFormOpen ? ' event-type-list--form-open' : ''}`}>
          {data.length > 0 &&
            (<ListViewComponent
              id="event-types-list"
              dataSource={[...eventTypeDataRef.current].sort((a, b) => a.Text.localeCompare(b.Text))}
              template={listTemplate}
              ref={listEventTypeRef}
            />)
          }
          {data.length === 0 && (
            <div className="d-flex justify-content-center align-items-center not-found-container">
              <div><FirmaLogo /></div>
              <div>
                <h3 className="inter-medium-16 text-color-grey-600">
                  {i18n.t('eventtype.not_found_title')}
                </h3>
                <p className="inter-regular-14 text-color-grey-600">
                  {i18n.t('eventtype.not_found_text')}{' '}
                  {i18n.t('eventtype.not_found_sub_text')}
                </p>
              </div>
            </div>
          )}
        </div>
        <div className="event-type-visualization-section mt-4">
          <h3 className="inter-semibold-16 text-color-blue-lefebvre mb-0">
            {i18n.t('eventtype.visualization_title')}
          </h3>
          <hr className="event-type-section-divider" />
          <p className="inter-regular-14 text-color-grey-800 mt-2 mb-4">
            {i18n.t('eventtype.visualization_description')}
          </p>
          <div className="event-type-viz-cards">
            <EventTypeVizCard
              name="colorMode"
              label={i18n.t('eventtype.colorMode_dot_title')}
              defaultLabel={i18n.t('eventtype.colorMode_dot_default')}
              description={i18n.t('eventtype.colorMode_dot_description')}
              checked={pendingColorMode === EVENT_TYPE_COLOR_MODES.DOT}
              onChange={() => setPendingColorMode(EVENT_TYPE_COLOR_MODES.DOT)}
              preview={
                <div className="evt-viz-event">
                  <div className="evt-viz-event__row">
                    <div>
                      <span className="evt-viz-event__time mr-2">{i18n.t('eventtype.meeting_committee_time')}</span>
                      <span className="evt-viz-event__title">{i18n.t('eventtype.meeting_committee')}</span>
                    </div>
                    <div className="evt-viz-event__dot"></div>
                  </div>
                  <div className="evt-viz-event__duration">{i18n.t('eventtype.meeting_committee_duration')}</div>
                </div>
              }
            />
            <EventTypeVizCard
              name="colorMode"
              label={i18n.t('eventtype.colorMode_background_title')}
              description={i18n.t('eventtype.colorMode_background_description')}
              checked={pendingColorMode === EVENT_TYPE_COLOR_MODES.BACKGROUND}
              className="evt-viz-card-background"
              onChange={() => setPendingColorMode(EVENT_TYPE_COLOR_MODES.BACKGROUND)}
              preview={
                <div className="evt-viz-event evt-viz-event--background">
                  <div className="evt-viz-event__row">
                    <div>
                      <span className="evt-viz-event__time mr-2">{i18n.t('eventtype.meeting_committee_time')}</span>
                      <span className="evt-viz-event__title">{i18n.t('eventtype.meeting_committee')}</span>
                    </div>
                  </div>
                  <div className="evt-viz-event__duration">{i18n.t('eventtype.meeting_committee_duration')}</div>
                </div>
              }
            />
          </div>
        </div>
      </div>

      <div className="mt-4 mb-4 btn-action-container">
        <Button
          className="btn-outline-primary inter-bold-16"
          title={i18n.t('general.close')}
          color="secondary"
          disabled={isFormOpen}
          onClick={cancel}
        >
          {i18n.t('general.close')}
        </Button>
        <Button
          className="ml-2 inter-bold-16"
          color="primary"
          disabled={isFormOpen}
          title={i18n.t('general.save')}
          onClick={handleSave}
        >
          {i18n.t('general.save')}
        </Button>
      </div>

      <Confirmation
        initialModalState={showNotification}
        onAccept={() => handleDeleteConfirm(deleteData)}
        onCancel={() => {
          setShowNotification(false);
          setDeleteData(undefined);
        }}
        message={i18n.t('schedule.eventTypeDeleteConfirmation')}
        titleAccept={i18n.t('general.remove')}
        titleCancel={i18n.t('general.cancel')}
        confirmType="confirm-info"
        iconType="lf-icon lf-icon-information"
      />
      <ToastComponent
        ref={(toast) => { toastRef.current = toast; }}
        id="toast_pos"
        content="Action successfully completed."
        position={TOAST_POSITION}
        animation={TOAST_ANIMATION}
        timeOut={7000}
        showCloseButton
      />
    </div>
  );
}

export { Eventtype };
export default Eventtype;
